import _initializerDefineProperty from "@babel/runtime/helpers/initializerDefineProperty";
import _defineProperty from "@babel/runtime/helpers/defineProperty";
import _applyDecoratedDescriptor from "@babel/runtime/helpers/applyDecoratedDescriptor";
import _initializerWarningHelper from "@babel/runtime/helpers/initializerWarningHelper";
var _dec, _class, _descriptor, _descriptor2, _descriptor3, _descriptor4, _descriptor5;
/*
 *                     NOTICE
 *            COPYRIGHT 2018 Ab Initio
 *       UNPUBLISHED - ALL RIGHTS RESERVED
 *
 * This program is confidential and a trade secret of Ab Initio.
 * The receipt or possession of this program does not convey any
 * rights to reproduce or disclose its contents, or to manufacture,
 * use or sell anything that it may describe, in whole or in part,
 * without the specific written consent of Ab Initio.
 */

import { AbActionBarMenuRCmp } from "abreact/incubator/ui/component/actionBar/AbActionBarMenuRCmp";
import { AbActionMenuSubmenu } from "abreact/incubator/ui/component/actionBar/AbActionMenuSubmenu";
import { BaseMachine } from "abreact/incubator/ui/machine/BaseMachine";
import { ClassUtils } from "abweb/utils/ClassUtils";
import { action, computed, observable, runInAction, makeObservable } from "mobx";

/**
 * Machine for a popup menu displayed by a Hover Action Menu.
 */
export let ActionBarMenuMachine = (_dec = observable.shallow, _class = class ActionBarMenuMachine extends BaseMachine {
  constructor(menuItem, menuItems, performActionFunc) {
    super();
    /**
     * Function to act on an item in this menu. This is passed in so that a common function can be used by all
     * objects.
     */
    _defineProperty(this, "_performAction", void 0);
    /**
     * The left position of this menu. This is initially set by the <code>{@link open()}</code> call, but can be changed
     * externally (for example, if the menu needs to be repositioned to move it fully onscreen). This is specified in
     * page coordinates.
     */
    _initializerDefineProperty(this, "left", _descriptor, this);
    /**
     * The top position of this menu. This is initially set by the <code>{@link open()}</code> call, but can be changed
     * externally (for example, if the menu needs to be repositioned to move it fully onscreen). This is specified in
     * page coordinates.
     */
    _initializerDefineProperty(this, "top", _descriptor2, this);
    /**
     * The menu that contains this one (if any)
     */
    _defineProperty(this, "_containingMenu", null);
    /**
     * Flag indicating whether this menu is open (i.e. displayed)
     * @type {boolean}
     * @private
     */
    _initializerDefineProperty(this, "_isOpen", _descriptor3, this);
    _initializerDefineProperty(this, "openHandle", _descriptor4, this);
    /**
     * The menu item that triggers this menu, if any. For the top-level popup menu, this is null. For all submenus,
     * this will be defined.
     */
    _defineProperty(this, "_menuItem", void 0);
    /**
     * The items in the menu
     */
    _initializerDefineProperty(this, "_menuItems", _descriptor5, this);
    makeObservable(this);
    this.left = undefined;
    this.top = undefined;
    this.openHandle = undefined;
    this._menuItems = undefined;
    this._isOpen = false;
    this._menuItem = menuItem;
    this.menuItems = menuItems;
    this._performAction = performActionFunc;
  }
  async destroyMenuItems() {
    for (const menuItem of this._menuItems) {
      if (ClassUtils.isInstanceOf(menuItem, ActionBarMenuMachine)) {
        await menuItem.destroy();
      }
    }
  }
  async destroy() {
    return this.destroyMenuItems().then(() => {
      super.destroy();
    });
  }

  /**
   * Flag indicating whether the menu is open.
   *
   * @returns {boolean}
   */
  get isOpen() {
    return this._isOpen;
  }

  /**
   * The menu item that triggers opening this menu (if any). For the top-level popup menu, this is null. For all submenus,
   * this will be defined.
   * @returns {AbActionMenuItem | null}
   */
  get menuItem() {
    return this._menuItem;
  }

  /**
   * The items in this menu
   *
   * @returns {Array<AbActionMenuItem | ActionBarMenuMachine>}
   */
  get menuItems() {
    return this._menuItems;
  }
  set menuItems(items) {
    // Convert submenu items to their own machines
    runInAction(() => {
      this._menuItems = items.map(menuItem => {
        if (ClassUtils.isInstanceOf(menuItem, AbActionMenuSubmenu)) {
          return new ActionBarMenuMachine(menuItem, menuItem.actions, (item, x, y) => this.performAction(x, y, item));
        }
        return menuItem;
      });
    });
    this.subMenuItems.forEach(subMenu => {
      subMenu._containingMenu = this;
    });
  }

  /**
   * The items in this menu that are submenus.
   *
   * @returns {Array<ActionBarMenuMachine>}
   */
  get subMenuItems() {
    return this._menuItems.filter(item => {
      return ClassUtils.isInstanceOf(item, ActionBarMenuMachine);
    });
  }

  /**
   * Perform the action for the specified item of this menu, taking into account the x/y coordinates where it was clicked.
   *
   * @param x       The page X position where the triggering click happened
   * @param y       The page Y position where the triggering click happened
   * @param item    The item to act on
   * @param target  An optional target HTML element
   */
  performAction(x, y, item, target) {
    this._performAction(item, x, y, target);
  }

  /**
   * Opens this menu at the specified page coordinates
   *
   * @param left  The desired left position (in page coordinates)
   * @param top   The desired top position (in page coordinates)
   */
  open(left, top) {
    if (this._containingMenu) {
      this._containingMenu.closeSubmenus();
    }
    this.left = left;
    this.top = top;
    this._isOpen = true;
    if (!this._containingMenu) {
      return AbActionBarMenuRCmp.showPopup(this);
    }
    return null;
  }

  /**
   * Closes this menu and any open submenus.
   */
  close() {
    if (this._isOpen) {
      this._isOpen = false;
      this.closeSubmenus();
      if (this.openHandle) {
        this.openHandle.close();
        runInAction(() => this.openHandle = null);
      }
    }
  }

  /**
   * Closes all submenus
   */
  closeSubmenus() {
    this.subMenuItems.forEach(it => {
      it.close();
    });
  }
  async getActionMenuItems(_) {
    return this._menuItems;
  }
  async getActionTarget() {
    return "";
  }
}, _descriptor = _applyDecoratedDescriptor(_class.prototype, "left", [observable], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: null
}), _descriptor2 = _applyDecoratedDescriptor(_class.prototype, "top", [observable], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: null
}), _descriptor3 = _applyDecoratedDescriptor(_class.prototype, "_isOpen", [observable], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: null
}), _descriptor4 = _applyDecoratedDescriptor(_class.prototype, "openHandle", [observable], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: null
}), _descriptor5 = _applyDecoratedDescriptor(_class.prototype, "_menuItems", [_dec], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: null
}), _applyDecoratedDescriptor(_class.prototype, "destroy", [action], Object.getOwnPropertyDescriptor(_class.prototype, "destroy"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "isOpen", [computed], Object.getOwnPropertyDescriptor(_class.prototype, "isOpen"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "open", [action], Object.getOwnPropertyDescriptor(_class.prototype, "open"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "close", [action], Object.getOwnPropertyDescriptor(_class.prototype, "close"), _class.prototype), _class);
//# sourceMappingURL=ActionBarMenuMachine.js.map
